import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import HomePage from "./pages/HomePage";
import Footer from "./components/Footer";
import TestSeriesPage from "./pages/TestSeriesPage";
import SmartContentsPage from "./pages/SmartContentsPage";
import CurrentAffairsPage from "./pages/CurrentAffairsPage";
import ClassroomPage from "./pages/ClassroomPage";
import OnlineClassesPage from "./pages/MasterClass";
import ClassDetailsPage from "./pages/ClassDetailsPage";
import RecordedClassesPage from "./pages/RecordedClasses";
import WatchClassPage from "./pages/WatchClass";
import MentoringPage from "./pages/MentoringClass";
import InterviewPrepPage from "./pages/InterviewPage";
import ResourcesPage from "./pages/ResourcesPage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import AuthPage from "./pages/AuthPage";
import CurrentAffairsDetailPage from "./pages/CurrentAffairsDetailPage";
import LiveClassPage from "./pages/JoinClass";
import NotFound from "./pages/NotFound";
// import Dashboard from "./pages/Dashboard";

// Import auth context
import { AuthProvider, useAuth } from "./context/AuthContext";

// Import animations
import AOS from "aos";
import "aos/dist/aos.css";

// Import toast notifications
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProfilePage from "./pages/ProfilePage";
import Dashboard from "./pages/Dashboard";
import StudentJitsiInterface from "./pages/StudentJitsiInterface";
import WatchSessionPage from "./pages/WatchSessionPage";
import ContentDetailPage from "./pages/ContentDetailPage";
import CurrentAffairsProvider from "./context/CurrentAffairsContext";
import SubjectTestsPage from "./pages/SubjectTestsPage";
import TestDetailsPage from "./pages/TestDetailsPage";
import TakeTestPage from "./pages/TakeTestPage";
import SeedFirestoreData from "./scripts/seedFirestore";
import TestResultsPage from "./pages/TestResultsPage";
import EnrollmentProvider from "./context/EnrollmentContext";

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppContent() {
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  return (
    <Router>
      <div className="flex flex-col min-h-screen bg-gray-50">
        <ToastContainer position="top-right" autoClose={3000} />
        <Navbar darkMode={true} />
        <main className="flex-grow">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/test-series" element={<TestSeriesPage />} />
            <Route path="/smart-content" element={<SmartContentsPage />} />
            <Route
              path="/digital-current-affairs"
              element={<CurrentAffairsPage type="daily" />}
            />
            <Route
              path="/daily-current-affairs"
              element={<CurrentAffairsPage type="daily" />}
            />
            <Route
              path="/monthly-current-affairs"
              element={<CurrentAffairsPage type="monthly" />}
            />
            <Route path="/classroom" element={<ClassroomPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/master-classes" element={<OnlineClassesPage />} />
            <Route path="/class-details/:id" element={<ClassDetailsPage />} />
            <Route path="/recorded-classes" element={<RecordedClassesPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blogdetails/:slug" element={<BlogDetailPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/register" element={<AuthPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/content/:id" element={<ContentDetailPage />} />
            <Route
              path="/admin/seed-database"
              element={<SeedFirestoreData />}
            />

            <Route
              path="/subject-tests/:subjectId"
              element={<SubjectTestsPage />}
            />
            <Route path="/test-details/:testId" element={<TestDetailsPage />} />
            <Route path="/take-test/:testId" element={<TakeTestPage />} />
            <Route
              path="/live-class/:id/join"
              element={<StudentJitsiInterface />}
            />
            <Route path="/watch-session/:id" element={<WatchSessionPage />} />
            <Route path="/test-results/:testId" element={<TestResultsPage />} />

            <Route
              path="/current-affairs/:id"
              element={<CurrentAffairsDetailPage />}
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mentoring"
              element={
                <ProtectedRoute>
                  <MentoringPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/interview"
              element={
                <ProtectedRoute>
                  <InterviewPrepPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/watch-class/:id"
              element={
                <ProtectedRoute>
                  <WatchClassPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/join-class/:id"
              element={
                <ProtectedRoute>
                  <LiveClassPage />
                </ProtectedRoute>
              }
            />

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <EnrollmentProvider>
        <CurrentAffairsProvider>
          <AppContent />
        </CurrentAffairsProvider>
      </EnrollmentProvider>
    </AuthProvider>
  );
}

export default App;
